/**
 * Tabs — WAI-ARIA tablist pattern.
 *
 *   <Tabs tabs={…} activeTab={id} onChange={setId} aria-label="…" />
 *   <TabPanel id={id} activeTab={activeTab}>…</TabPanel>
 *
 * Keyboard:
 *   Left/Right  — move focus & activate previous/next tab (wraps)
 *   Home/End    — jump to first/last tab
 *   Tab         — leave the tablist and land in the active panel
 *
 * We use `aria-activedescendant`-free, roving-tabindex style: the active
 * tab has `tabIndex={0}`, inactives have `tabIndex={-1}`. That way shift-tab
 * out lands the next stop, not the first tab.
 */
import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface Tab {
  id: string;
  label: string;
  count?: number;
}

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (id: string) => void;
  className?: string;
  /** Accessible name for the tablist (announced by screen readers). */
  "aria-label"?: string;
  /** Id prefix used to wire tabs to their panels via aria-controls. */
  idPrefix?: string;
}

export function Tabs({
  tabs,
  activeTab,
  onChange,
  className,
  "aria-label": ariaLabel,
  idPrefix = "tab",
}: TabsProps) {
  const buttonsRef = useRef<Array<HTMLButtonElement | null>>([]);

  const focusTab = (index: number) => {
    const wrapped = (index + tabs.length) % tabs.length;
    const next = tabs[wrapped];
    if (!next) return;
    onChange(next.id);
    // Wait a frame for re-render so tabIndex is updated, then focus.
    requestAnimationFrame(() => buttonsRef.current[wrapped]?.focus());
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        focusTab(index + 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        focusTab(index - 1);
        break;
      case "Home":
        e.preventDefault();
        focusTab(0);
        break;
      case "End":
        e.preventDefault();
        focusTab(tabs.length - 1);
        break;
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      className={cn(
        // Horizontal scroll on mobile so long tab strips (Settings has
        // ~18 tabs) don't wrap or blow out the viewport. The scrollbar
        // is hidden on desktop where everything fits.
        "flex gap-1 border-b border-border overflow-x-auto whitespace-nowrap",
        className
      )}
    >
      {tabs.map((tab, index) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              buttonsRef.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`${idPrefix}-${tab.id}-panel`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              "px-4 py-2 text-sm font-medium transition-colors relative",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-t",
              isActive
                ? "text-accent"
                : "text-text-muted hover:text-text-primary"
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={cn(
                  "ml-1.5 px-1.5 py-0.5 rounded-full text-xs",
                  isActive
                    ? "bg-accent/10 text-accent"
                    : "bg-border text-text-muted"
                )}
              >
                {tab.count}
              </span>
            )}
            {isActive && (
              <div
                aria-hidden="true"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent rounded-t-full"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

interface TabPanelProps {
  id: string;
  activeTab: string;
  idPrefix?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Matching tabpanel container. Render one per tab; each hides itself unless
 * active. Using a container div (rather than conditionally rendering children)
 * keeps the DOM stable for tools like Radix/Headless and preserves scroll
 * positions if the consumer wants.
 */
export function TabPanel({
  id,
  activeTab,
  idPrefix = "tab",
  className,
  children,
}: TabPanelProps) {
  const isActive = activeTab === id;
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-${id}-panel`}
      aria-labelledby={`${idPrefix}-${id}`}
      tabIndex={0}
      hidden={!isActive}
      className={cn("focus:outline-none", className)}
    >
      {isActive ? children : null}
    </div>
  );
}
